import type { IrsStatus } from '@divorceos/schema';
import type { YearData } from './data.js';
import { cents, clamp0, max, min } from './schedule.js';
import { scheduleTax } from './schedule.js';

export type Lines = Record<string, number>;

export function ordinaryTax(d: YearData, st: IrsStatus, x: number): number {
  return scheduleTax(d.bracketTops[st], d.rates, x).tax;
}

/** Inputs shared by the two capital-gain tax worksheets. */
export interface CapGainInputs {
  taxableIncome: number;
  qualifiedDividends: number;
  /** Schedule D line 15 (net long-term gain) and line 16 (net gain). */
  netLongTerm: number;
  netGain: number;
  /** Schedule D lines 18 / 19. */
  rateGain28: number;
  unrecaptured1250: number;
  form4952Line4e: number;
  form4952Line4g: number;
}

/** What Form 6251 Part III needs from whichever regular-tax worksheet ran. */
export interface CapGainSummary {
  tax: number;
  /** QD&CG line 4 or Sch D Tax Wks line 13: amount taxed at 0/15/20%. */
  preferential: number;
  /** Sch D Tax Wks line 10 (qualified dividends + net capital gain); same as preferential for QD&CG. */
  line10: number;
  /** QD&CG line 5 or Sch D Tax Wks line 14: amount at ordinary rates (incl. 28%/25% parts for Sch D wks). */
  ordinaryPortion: number;
  /** QD&CG line 5 or Sch D Tax Wks line 19: base used for the 15% breakpoint test. */
  line19: number;
  unrecaptured1250: number;
  usedScheduleDWorksheet: boolean;
}

/** Qualified Dividends and Capital Gain Tax Worksheet (Form 1040 instructions, line 16). */
export function qdcgWorksheet(d: YearData, st: IrsStatus, c: CapGainInputs, L: Lines): CapGainSummary {
  const cg = d.capitalGains;
  const l1 = c.taxableIncome;
  const l2 = clamp0(c.qualifiedDividends);
  const l3 = c.netLongTerm > 0 && c.netGain > 0 ? min(c.netLongTerm, c.netGain) : 0;
  const l4 = l2 + l3;
  const l5 = clamp0(l1 - l4);
  const l6 = cg.zeroRateTop[st];
  const l7 = min(l1, l6);
  const l8 = min(l5, l7);
  const l9 = clamp0(l7 - l8); // taxed at 0%
  const l10 = min(l1, l4);
  const l11 = l9;
  const l12 = clamp0(l10 - l11);
  const l13 = cg.fifteenRateTop[st];
  const l14 = min(l1, l13);
  const l15 = l5 + l9;
  const l16 = clamp0(l14 - l15);
  const l17 = min(l12, l16);
  const l18 = cents(l17 * cg.rate15);
  const l19 = l9 + l17;
  const l20 = clamp0(l10 - l19);
  const l21 = cents(l20 * cg.rate20);
  const l22 = ordinaryTax(d, st, l5);
  const l23 = cents(l18 + l21 + l22);
  const l24 = ordinaryTax(d, st, l1);
  const l25 = min(l23, l24);
  Object.assign(L, {
    'QDCG.1': l1, 'QDCG.2': l2, 'QDCG.3': l3, 'QDCG.4': l4, 'QDCG.5': l5, 'QDCG.7': l7, 'QDCG.9': l9,
    'QDCG.12': l12, 'QDCG.17': l17, 'QDCG.18': l18, 'QDCG.20': l20, 'QDCG.21': l21, 'QDCG.22': l22,
    'QDCG.23': l23, 'QDCG.24': l24, 'QDCG.25': l25,
  });
  return { tax: l25, preferential: l4, line10: l4, ordinaryPortion: l5, line19: l5, unrecaptured1250: 0, usedScheduleDWorksheet: false };
}

/** Schedule D Tax Worksheet (Schedule D instructions) — handles 28% rate gain, unrecaptured 1250 and Form 4952 4g. */
export function scheduleDTaxWorksheet(d: YearData, st: IrsStatus, c: CapGainInputs, L: Lines): CapGainSummary {
  const cg = d.capitalGains;
  const l1 = c.taxableIncome;
  const l2 = clamp0(c.qualifiedDividends);
  const l3 = clamp0(c.form4952Line4g);
  const l4 = clamp0(c.form4952Line4e);
  const l5 = clamp0(l3 - l4);
  const l6 = clamp0(l2 - l5);
  const l7 = c.netLongTerm > 0 && c.netGain > 0 ? min(c.netLongTerm, c.netGain) : 0;
  const l8 = min(l3, l4);
  const l9 = clamp0(l7 - l8);
  const l10 = l6 + l9;
  const sch18 = clamp0(c.rateGain28);
  const sch19 = clamp0(c.unrecaptured1250);
  const l11 = sch18 + sch19;
  const l12 = min(l9, l11);
  const l13 = l10 - l12;
  const l14 = clamp0(l1 - l13);
  const l15 = cg.zeroRateTop[st];
  const l16 = min(l1, l15);
  const l17 = min(l14, l16);
  const l18 = clamp0(l1 - l10);
  const l19 = max(l17, l18);
  const l20 = clamp0(l16 - l17); // 0%
  const l21 = min(l1, l13);
  const l22 = l20;
  const l23 = clamp0(l21 - l22);
  const l24 = cg.fifteenRateTop[st];
  const l25 = min(l1, l24);
  const l26 = l19 + l20;
  const l27 = clamp0(l25 - l26);
  const l28 = min(l23, l27);
  const l29 = cents(l28 * cg.rate15);
  const l30 = l22 + l28;
  const l31 = clamp0(l21 - l30);
  const l32 = cents(l31 * cg.rate20);
  let l37 = 0, l38 = 0;
  if (sch19 > 0) {
    const l33 = min(l9, sch19);
    const l34 = l10 + l19;
    const l35 = l1;
    const l36 = clamp0(l34 - l35);
    l37 = clamp0(l33 - l36);
    l38 = cents(l37 * cg.rateUnrecaptured1250);
  }
  let l41 = 0;
  if (sch18 > 0) {
    const l39 = l19 + l20 + l28 + l31 + l37;
    const l40 = clamp0(l1 - l39);
    l41 = cents(l40 * cg.rate28);
  }
  const l42 = ordinaryTax(d, st, l19);
  const l43 = cents(l29 + l32 + l38 + l41 + l42);
  const l44 = ordinaryTax(d, st, l1);
  const l45 = min(l43, l44);
  Object.assign(L, {
    'SchDWks.1': l1, 'SchDWks.10': l10, 'SchDWks.13': l13, 'SchDWks.14': l14, 'SchDWks.19': l19, 'SchDWks.20': l20,
    'SchDWks.28': l28, 'SchDWks.29': l29, 'SchDWks.31': l31, 'SchDWks.32': l32, 'SchDWks.37': l37, 'SchDWks.38': l38,
    'SchDWks.41': l41, 'SchDWks.42': l42, 'SchDWks.43': l43, 'SchDWks.44': l44, 'SchDWks.45': l45,
  });
  return { tax: l45, preferential: l13, line10: l10, ordinaryPortion: l14, line19: l19, unrecaptured1250: sch19, usedScheduleDWorksheet: true };
}

/** Form 6251 lines 7/39: 26% up to the breakpoint, 28% above (less 2% x breakpoint). */
export function amtRateTax(d: YearData, st: IrsStatus, x: number): number {
  if (x <= 0) return 0;
  const bp = d.amt.highRateBreakpoint[st];
  if (x <= bp) return cents(x * d.amt.lowRate);
  return cents(x * d.amt.highRate - (d.amt.highRate - d.amt.lowRate) * bp);
}

/** Form 6251 Part III (tax computation using maximum capital gains rates). Returns line 40. */
export function amtPartIII(d: YearData, st: IrsStatus, line6: number, cg: CapGainSummary, L: Lines): number {
  const cgd = d.capitalGains;
  const l12 = line6;
  const l13 = cg.preferential;
  const l14 = cg.unrecaptured1250;
  const l15 = cg.usedScheduleDWorksheet ? min(l13 + l14, cg.line10) : l13;
  const l16 = min(l12, l15);
  const l17 = clamp0(l12 - l16);
  const l18 = amtRateTax(d, st, l17);
  const l19 = cgd.zeroRateTop[st];
  const l20 = cg.ordinaryPortion;
  const l21 = clamp0(l19 - l20);
  const l22 = min(l12, l13);
  const l23 = min(l21, l22); // 0%
  const l24 = clamp0(l22 - l23);
  const l25 = cgd.fifteenRateTop[st];
  const l26 = l21;
  const l27 = cg.line19;
  const l28 = l26 + l27;
  const l29 = clamp0(l25 - l28);
  const l30 = min(l24, l29);
  const l31 = cents(l30 * cgd.rate15);
  const l32 = l23 + l30;
  let l33 = 0, l34 = 0;
  if (Math.abs(l32 - l12) > 0.005) {
    l33 = clamp0(l22 - l32);
    l34 = cents(l33 * cgd.rate20);
  }
  let l37 = 0;
  if (l14 > 0) {
    const l35 = l17 + l32 + l33;
    const l36 = clamp0(l12 - l35);
    l37 = cents(l36 * cgd.rateUnrecaptured1250);
  }
  const l38 = cents(l18 + l31 + l34 + l37);
  const l39 = amtRateTax(d, st, l12);
  const l40 = min(l38, l39);
  Object.assign(L, {
    '6251.12': l12, '6251.13': l13, '6251.15': l15, '6251.17': l17, '6251.18': l18, '6251.23': l23, '6251.30': l30,
    '6251.31': l31, '6251.33': l33, '6251.34': l34, '6251.37': l37, '6251.38': l38, '6251.39': l39, '6251.40': l40,
  });
  return l40;
}
